import { z } from "zod";
import {
  AccessResponse,
  AuthResponse,
  ChatConfig,
  ChatMessage,
  ChatModerationInput,
  ChatPage,
  ChatSendInput,
  CrashXError,
  ERROR_CODES,
  LoginInput,
  RainEvent,
  RainInput,
  RainOffer,
  RainPrepareInput,
  RegisterInput,
  SuccessResponse,
} from "@crashx/shared";

async function request(path: string, options: RequestInit = {}): Promise<unknown> {
  const response = await fetch(`/api/v1/${path}`, {
    credentials: "same-origin",
    cache: "no-store",
    signal: AbortSignal.timeout(10000),
    ...options,
  });
  const value: unknown = await response.json();
  if (!response.ok) {
    const error = z.object({ error: z.object({ code: z.enum(ERROR_CODES) }) }).safeParse(value);
    throw new CrashXError(error.success ? error.data.error.code : "INTERNAL");
  }
  return value;
}
let csrfFlight: Promise<string> | null = null;
async function csrf() {
  const cookie = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("crashx_csrf="));
  if (cookie) return decodeURIComponent(cookie.slice("crashx_csrf=".length));
  csrfFlight ??= request("auth/csrf")
    .then((value) => z.object({ csrfToken: z.string() }).parse(value).csrfToken)
    .finally(() => {
      csrfFlight = null;
    });
  return csrfFlight;
}
async function mutate(path: string, body: unknown) {
  const csrfToken = await csrf();
  return request(path, {
    method: "POST",
    headers: { "content-type": "application/json", "x-csrf-token": csrfToken },
    body: JSON.stringify(body),
  });
}
export const chatApi = {
  config: async () => ChatConfig.parse(await request("chat/config")),
  access: async () => AccessResponse.parse(await request("me/access")),
  history: async (cursor?: string) =>
    ChatPage.parse(
      await request(`chat/messages?limit=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`),
    ),
  send: async (text: string) =>
    ChatMessage.parse(await mutate("chat/messages", ChatSendInput.parse({ text }))),
  moderate: async (input: z.input<typeof ChatModerationInput>) =>
    SuccessResponse.parse(await mutate("chat/moderation", ChatModerationInput.parse(input))),
  prepare: async (input: RainPrepareInput) =>
    RainOffer.parse(await mutate("chat/rain/prepare", RainPrepareInput.parse(input))),
  rain: async (input: RainInput) => RainEvent.parse(await mutate("chat/rain", RainInput.parse(input))),
  login: async (input: unknown) => AuthResponse.parse(await mutate("auth/login", LoginInput.parse(input))),
  register: async (input: unknown) => {
    await mutate("auth/register", RegisterInput.parse(input));
  },
};
export function chatError(error: unknown): string {
  if (error instanceof CrashXError) return `errors.${error.code}`;
  if (error instanceof z.ZodError) return "errors.VALIDATION";
  return "chat.requestFailed";
}
